import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MapPinOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useDocgenStore } from "@/features/docgen/useDocgenStore";
import { DocgenView } from "@/features/docgen/DocgenView";
import { AmendmentSummary } from "@/features/docgen/AmendmentSummary";
import { Mapa1Panel } from "@/features/docgen/Mapa1Panel";
import { DEMO_DOCGEN } from "@/features/docgen/fixtures";
import { PlanUploadDialog } from "@/features/feasibility/PlanUploadDialog";

/**
 * Step 3 — Projektna dokumentacija. The DocgenView itself is data-
 * agnostic; this page handles bootstrap (load from store, hydrate
 * the demo fixture for ``?demo=1``, or invite the user back to Step
 * 2 if there's nothing to show).
 */
export function DocsPage() {
  const [searchParams] = useSearchParams();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const data = useDocgenStore((s) => s.data);
  const setData = useDocgenStore((s) => s.setData);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planActivated, setPlanActivated] = useState<{ name: string } | null>(
    null,
  );

  useEffect(() => {
    if (!data && searchParams.get("demo") === "1") {
      setData(DEMO_DOCGEN);
    }
  }, [data, searchParams, setData]);

  if (!data) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-8 text-center shadow-sm">
        <h2 className="text-lg font-bold text-stone-800">Nema generiranih dokumenata</h2>
        <p className="mt-2 text-stone-500">
          Vrati se na Korak 2 i unesi podatke o projektu, ili otvori demo prikaz s
          fiksnim podacima.
        </p>
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button asChild variant="outline">
            <Link to="../provjeri">Natrag na provjeru</Link>
          </Button>
          <Button asChild>
            <Link to="?demo=1">Učitaj demo</Link>
          </Button>
        </div>
      </div>
    );
  }

  // R1 — plan-mismatch guard: the fact sheet flags when no spatial plan
  // could be keyed for this project; the documents then carry the
  // plan-missing placeholder instead of citing any plan.
  const factSheet = (data.fact_sheet ?? {}) as {
    municipality_unknown?: boolean;
    plan_placeholder?: string | null;
    katastarski_opcina?: string | null;
  };

  return (
    <>
      {/* R4 — plan activated in-session: offer regeneration (never auto).
          The municipality is already written onto the draft, so Provjeri's
          Generiraj will cite the real plan. */}
      {planActivated && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-green-300 bg-green-50 px-4 py-3 text-[0.85rem] text-green-900">
          <span className="min-w-0 flex-1">
            Plan za <strong>{planActivated.name}</strong> je aktivan. Ovi
            dokumenti su izrađeni prije plana — regeneriraj ih da dobiju citate
            plana.
          </span>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate(`/project/${projectId}/provjeri`)}
          >
            Regeneriraj dokumente
          </Button>
        </div>
      )}

      {factSheet.municipality_unknown && !planActivated && (
        <div className="mb-3 flex flex-wrap items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-[0.85rem] text-amber-900">
          <MapPinOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              Prostorni plan{" "}
              {factSheet.katastarski_opcina
                ? `za k.o. ${factSheet.katastarski_opcina} `
                : ""}
              nije u Permitko bazi — dokumenti sadrže oznaku{" "}
              <span className="font-mono text-[0.78rem]">
                {factSheet.plan_placeholder ?? "[PROSTORNI PLAN NIJE U BAZI]"}
              </span>{" "}
              umjesto citata plana.
            </p>
            <div className="mt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-900 hover:bg-amber-100"
                onClick={() => setPlanDialogOpen(true)}
              >
                Učitaj prostorni plan
              </Button>
            </div>
          </div>
        </div>
      )}
      <AmendmentSummary data={data} />

      {/* Ivo's ask — the whole Mapa 1 as one bound PDF (his uploads + our
          generated docs, in order, with a matching Sadržaj). Shown above the
          per-document view so "download the whole folder" is the headline. */}
      <div className="mb-4">
        <Mapa1Panel projectId={projectId} data={data} />
      </div>

      <DocgenView data={data} />

      <PlanUploadDialog
        open={planDialogOpen}
        onOpenChange={setPlanDialogOpen}
        projectId={projectId}
        initialJlsName={factSheet.katastarski_opcina ?? undefined}
        onActivated={(_code, name) => {
          setPlanActivated({ name });
          setPlanDialogOpen(false);
        }}
      />
    </>
  );
}
